import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { IDashboardItemFactory } from 'src/app/utils/dashboard-item';
import type { IKpiTilesDashboardItem } from './kpi-tiles';

export type KpiTileTone = 'positive' | 'negative' | 'neutral';
export type KpiTileIndicator = 'up' | 'down' | 'none';

/** One KPI tile. */
export interface KpiTileViewModel {
    label: string;
    value: number;
    /** Optional unit shown under the value (currency name, count noun, etc.). */
    unit?: string;
    /** Tile accent colour. Defaults to neutral. */
    tone?: KpiTileTone;
    /** Optional head indicator, independent from the accent colour. Defaults to none. */
    indicator?: KpiTileIndicator;
    /** Formats the main value; defaults to an integer number. */
    valueFormatter?: (value: number) => string;
    /** Change ratio (0.12 = +12%); null hides the delta. */
    delta?: number | null;
    /** Caption after the delta, e.g. "vs 1st half". */
    deltaCaption?: string;
    /** Delta colour; by default derives from the delta sign. */
    deltaTone?: KpiTileTone;
    /** Formats the delta magnitude; defaults to a rounded percentage. */
    deltaFormatter?: (delta: number) => string;
    /** HTML for the tile's documentation (info-circle) icon; omitted hides the icon. */
    documentation?: string;
}

/** Generic, reusable KPI-tile row driven by a standard dashboard-item/data-loader link. */
@Component({
    selector: 'app-kpi-tiles',
    templateUrl: './kpi-tiles.component.html',
    styleUrls: ['./kpi-tiles.component.scss'],
})
export class KpiTilesComponent implements OnInit, OnDestroy {
    @Input()
    dashboardItemFactory!: IDashboardItemFactory;

    @Input()
    dataLoaderEmitter!: EventEmitter<IDataLoaderResult>;

    dashboardItem!: IKpiTilesDashboardItem;
    tiles: KpiTileViewModel[] = [];

    private unsubscribeDataLoader?: () => void;

    constructor(private modal: NzModalService) {}

    ngOnInit(): void {
        this.dashboardItem =
            this.dashboardItemFactory.create() as IKpiTilesDashboardItem;
        const subscription = this.dataLoaderEmitter.subscribe(
            (dataLoaderResult) => {
                this.applyDataLoaderResult(dataLoaderResult);
            }
        );
        this.unsubscribeDataLoader = () => subscription.unsubscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribeDataLoader?.();
    }

    private applyDataLoaderResult(dataLoaderResult: IDataLoaderResult): void {
        if (dataLoaderResult.isLoading) {
            return;
        }
        if (dataLoaderResult.status !== 'ok') {
            this.tiles = [];
            return;
        }
        this.tiles = this.dashboardItem.composeData(
            dataLoaderResult.data,
            dataLoaderResult.queryContext
        );
    }

    indicator(t: KpiTileViewModel): string {
        return t.indicator === 'up' ? '▲' : t.indicator === 'down' ? '▼' : '';
    }

    deltaTone(t: KpiTileViewModel): KpiTileTone {
        if (t.deltaTone) {
            return t.deltaTone;
        }
        return (t.delta ?? 0) >= 0 ? 'positive' : 'negative';
    }

    deltaIndicator(t: KpiTileViewModel): string {
        return (t.delta ?? 0) >= 0 ? '▲' : '▼';
    }

    /** Same documentation modal as the chart/table cards, titled with the tile label. */
    showTileDocumentation(t: KpiTileViewModel): void {
        DashboardService.showChartDocumentation(
            this.modal,
            t.label,
            t.documentation
        );
    }

    formatDelta(t: KpiTileViewModel): string {
        const delta = t.delta ?? 0;
        return t.deltaFormatter
            ? t.deltaFormatter(delta)
            : `${Math.round(Math.abs(delta) * 100)}%`;
    }
}
